import {StaffSessionModel, staffCan} from '../../core/staffAccess'
export type TaxpayerType = 1 | 2 | 3 | 4

export type FieldErrors = Record<string, string>

export type DuplicateBusinessAction = 'createNew' | 'addFiscalMemory'

export interface TaxpayerTypeOption {
  value: TaxpayerType
  title: string
  description: string
  economicLength: number
  nationalLength: number
  nationalLabel: string
  icon: string
  // When set, the national identifier must also satisfy the Iranian national-code checksum.
  nationalCodeChecksum?: boolean
  // How the economic code has to relate to the national identifier, when the two are tied.
  economicCodeRule?: 'startsWithNational' | 'equalsNational'
}

export const taxpayerTypes: TaxpayerTypeOption[] = [
  {
    value: 1,
    title: 'شخص حقیقی',
    description: 'کسب‌وکار متعلق به یک فرد ایرانی',
    economicLength: 14,
    nationalLength: 10,
    nationalLabel: 'کد ملی',
    icon: 'la-user',
    nationalCodeChecksum: true,
    economicCodeRule: 'startsWithNational',
  },
  {
    value: 2,
    title: 'شخص حقوقی',
    description: 'شرکت، مؤسسه یا سازمان ثبت‌شده',
    economicLength: 11,
    nationalLength: 11,
    nationalLabel: 'شناسه ملی',
    icon: 'la-building',
    economicCodeRule: 'equalsNational',
  },
  {
    value: 3,
    title: 'مشارکت مدنی',
    description: 'پرونده مالیاتی مربوط به مشارکت مدنی',
    economicLength: 11,
    nationalLength: 10,
    nationalLabel: 'کد ملی مشارکت',
    icon: 'la-user-friends',
    nationalCodeChecksum: true,
  },
  {
    value: 4,
    title: 'تبعه غیرایرانی',
    description: 'شخص حقیقی دارای کد فراگیر اتباع',
    economicLength: 14,
    nationalLength: 12,
    nationalLabel: 'کد فراگیر',
    icon: 'la-globe',
  },
]

export const normalizeIdentifier = (value: string, alphaNumeric = false): string => {
  const persianDigits = '۰۱۲۳۴۵۶۷۸۹'
  const arabicDigits = '٠١٢٣٤٥٦٧٨٩'
  const normalized = (value || '')
    .trim()
    .replace(/[۰-۹]/g, (digit) => String(persianDigits.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(arabicDigits.indexOf(digit)))
    .replace(/[\s\-_/.\u200c\u200d\u200e\u200f]/g, '')
    .toUpperCase()

  return normalized.replace(alphaNumeric ? /[^0-9A-Z]/g : /[^0-9]/g, '')
}

export const normalizeMobile = (value: string): string => {
  let normalized = normalizeIdentifier(value)
  if (normalized.startsWith('0098')) normalized = `0${normalized.slice(4)}`
  else if (normalized.startsWith('98')) normalized = `0${normalized.slice(2)}`
  else if (normalized.length === 10 && normalized.startsWith('9')) normalized = `0${normalized}`
  return normalized
}

export const isValidIranianNationalCode = (value: string): boolean => {
  const nationalCode = normalizeIdentifier(value)
  if (nationalCode.length !== 10 || /^(\d)\1{9}$/.test(nationalCode)) return false

  const remainder =
    nationalCode
      .slice(0, 9)
      .split('')
      .reduce((sum, digit, index) => sum + Number(digit) * (10 - index), 0) % 11

  return Number(nationalCode[9]) === (remainder < 2 ? remainder : 11 - remainder)
}

export const getTaxpayerTypeOption = (taxpayerType: TaxpayerType): TaxpayerTypeOption =>
  taxpayerTypes.find((item) => item.value === taxpayerType)!

/**
 * The type the lengths of the two identifiers point to (14/10 حقیقی, 11/11 حقوقی, 11/10 مشارکت
 * مدنی, 14/12 اتباع), or undefined. Each pair fits one type only, so a form follows it instead of
 * asking. Mirrors BusinessRegistrationRules.ResolveTaxpayerType on the backend.
 */
export const taxpayerTypeFromLengths = (
  economicCode: string,
  nationalIdentifier: string
): TaxpayerType | undefined => {
  const economic = normalizeIdentifier(economicCode)
  const national = normalizeIdentifier(nationalIdentifier)
  if (economic.length === 11 && national.length === 11 && economic === national) return 2
  if (economic.length === 14 && national.length === 10) return 1
  if (economic.length === 11 && national.length === 10) return 3
  if (economic.length === 14 && national.length === 12) return 4
  return undefined
}

/**
 * The type a valid pair proves (lengths, checksum and the economic code's tie to the national
 * identifier), or undefined. Mirrors BusinessRegistrationRules.DetectTaxpayerType.
 */
export const detectTaxpayerType = (
  economicCode: string,
  nationalIdentifier: string
): TaxpayerType | undefined => {
  const type = taxpayerTypeFromLengths(economicCode, nationalIdentifier)
  return type &&
    Object.keys(validateTaxpayerIdentifiers(type, economicCode, nationalIdentifier)).length === 0
    ? type
    : undefined
}

export const validateBusinessIdentity = (
  taxpayerType: TaxpayerType,
  economicCode: string,
  nationalIdentifier: string,
  fiscalId: string
): FieldErrors => {
  const errors = validateTaxpayerIdentifiers(taxpayerType, economicCode, nationalIdentifier)
  if (normalizeIdentifier(fiscalId, true).length !== 6) {
    errors.fiscalId = 'شناسه یکتای حافظه مالیاتی باید دقیقاً ۶ کاراکتر باشد.'
  }
  return errors
}

export const validateTaxpayerIdentifiers = (
  taxpayerType: TaxpayerType,
  economicCode: string,
  nationalIdentifier: string
): FieldErrors => {
  const option = getTaxpayerTypeOption(taxpayerType)
  const errors: FieldErrors = {}
  const normalizedEconomicCode = normalizeIdentifier(economicCode)
  const normalizedNationalIdentifier = normalizeIdentifier(nationalIdentifier)

  if (normalizedEconomicCode.length !== option.economicLength) {
    errors.economicCode = `شماره اقتصادی باید دقیقاً ${option.economicLength} رقم باشد.`
  }
  if (normalizedNationalIdentifier.length !== option.nationalLength) {
    errors.nationalIdentifier = `${option.nationalLabel} باید دقیقاً ${option.nationalLength} رقم باشد.`
  } else if (
    option.nationalCodeChecksum &&
    !isValidIranianNationalCode(normalizedNationalIdentifier)
  ) {
    errors.nationalIdentifier = `${option.nationalLabel} معتبر نیست.`
  }

  // Only worth comparing the two once each one is individually well formed.
  if (!errors.economicCode && !errors.nationalIdentifier) {
    if (
      option.economicCodeRule === 'startsWithNational' &&
      !normalizedEconomicCode.startsWith(normalizedNationalIdentifier)
    ) {
      errors.economicCode = `${option.nationalLength} رقم نخست شماره اقتصادی باید با ${option.nationalLabel} یکسان باشد.`
    } else if (
      option.economicCodeRule === 'equalsNational' &&
      normalizedEconomicCode !== normalizedNationalIdentifier
    ) {
      errors.economicCode = `شماره اقتصادی باید با ${option.nationalLabel} یکسان باشد.`
    }
  }

  return errors
}

export const createIdempotencyKey = (): string => {
  const cryptoApi = window.crypto as Crypto & {randomUUID?: () => string}
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID()
  const bytes = new Uint8Array(16)
  cryptoApi.getRandomValues(bytes)
  return Array.from(bytes)
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

// Registering a business is «کسب‌وکارها» at «مدیریت» (docs/staff-access.md); an on-premises build's administrator
// signs in with every section.
export const canRegisterBusiness = (user?: {staff?: StaffSessionModel | null} | null): boolean =>
  staffCan(user, 'businesses', 'manage')

export const canConfirmDuplicateBusiness = (
  errorCode?: string,
  existingCompanyId?: number
): boolean => errorCode === 'duplicate_confirmation_required' && Number(existingCompanyId) > 0

const invalidInquiryCodes = new Set([
  'inquiry_invalid',
  'inquiry_changed',
  'inquiry_expired',
  'inquiry_environment_changed',
  'inquiry_consumed',
  'inquiry_not_found',
])

export const requiresFreshInquiry = (errorCode?: string): boolean =>
  Boolean(errorCode && invalidInquiryCodes.has(errorCode))
